import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { defineMessages, FormattedDate, FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { useBlockedUsers } from '../../../graphdebate/src/hooks/useBlockedUsers';
import type { AvatarPropsType } from '../components/Avatar';
import { Tooltip } from '../components/GraphModal/Tooltip';
import type { TimeRenderFn } from '../components/LimitedRelativeTime';
import { LimitedRelativeTime } from '../components/LimitedRelativeTime';
import type { Author, User } from '../types/api';
import { AuthorAvatar } from './AuthorAvatar';
import { AuthorAvatarPseudo } from './AuthorAvatarPseudo';
import { AuthorPseudo } from './AuthorPseudo';
import { useThreadIsRoot } from './ThreadContexts/ThreadIsRootContext';

const timeMessages = defineMessages({
  authorDate: {
    defaultMessage: `on {formattedDate}`,
    id: 'wrSgtN',
  },
  // answeredAuthorDate: {
  //   defaultMessage: `a répondu à {renderAnsweredAuthor} {authorDate}`,
  //   id: '86PvPY',
  // },
  answeredAuthorDate: {
    defaultMessage: `{renderAnsweredAuthor} {authorDate}`,
    id: '5Zb7eB',
  },
});

const CustomAnswerLead = () => {
  return (
    <div className="CustomAnswerLead h-full w-3 min-w-3 relative">
      <div className="absolute right-0 -top-2 h-[21px] w-1/2 border-b-2 border-l-2 border-gray-250 rounded-bl"></div>
    </div>
  );
};

export const ThreadAuthoringDetails = ({
  author,
  answeredAuthor,
  isNestedAnswer,
  date,
  isRelevanceSort,
  ...others
}: {
  author?: Author;
  answeredAuthor?: Author;
  isNestedAnswer?: boolean;
  date?: Date;
  isRelevanceSort?: boolean;
} & Omit<AvatarPropsType, 'picture' | 'size' | 'legacyImage'>) => {
  const isRoot = useThreadIsRoot();
  const { isBlocked, isNative } = useBlockedUsers();
  const isAuthorBlocked = isNative && isBlocked((author as User)?.id);
  const isAnsweredAuthorBlocked = isNative && isBlocked((answeredAuthor as User)?.id);

  // B8_05 : ISO pour l'attribut `<time datetime>` (SSR, cohérent avec le JSON-LD).
  // ⚠ `date` est typé `Date` mais reçoit en pratique un `createdAt` GraphQLDateTime = string ISO
  //    (cf. ThreadInteractiveTopic/Message). On normalise et on écarte les dates invalides
  //    pour ne jamais crasher sur `.toISOString()`.
  const dateTimeIso = useMemo(() => {
    if (date == null) return undefined;
    const d = date instanceof Date ? date : new Date(date);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  }, [date]);

  const renderAuthorAvatar = useMemo(() => {
    return author ? (
      <AuthorAvatar
        author={author}
        userId={(author as User).id}
        profile={(author as User).profile}
        withProfilePopover
        size={!isRelevanceSort ? 'small2' : 'medium1'}
        className="Avatar1"
        isUserBlocked={isAuthorBlocked}
        {...others}
      />
    ) : null;
  }, [author, isRelevanceSort, others, isAuthorBlocked]);

  const renderAnsweringAuthorAvatarPseudo = useMemo(() => {
    return author ? (
      <AuthorAvatarPseudo
        author={author}
        userId={(author as User).id}
        profile={(author as User).profile}
        firstChild
        withProfileLink
        className="AvatarPseudo2 max-w-full"
        isUserBlocked={isAnsweredAuthorBlocked}
      />
    ) : null;
  }, [author, isAnsweredAuthorBlocked]);

  const renderAuthoringDetails: TimeRenderFn = useCallback(
    ({ formattedDate, relativeOrExact }) => {
      // e.g. "30/12/2022" or "il y a 1 h"
      // B8_05 : la date de publication est rendue en SSR dans un `<time datetime="<ISO>">`
      // (cohérent avec `datePublished` du JSON-LD, cf. TopicLd), l'affichage humain (relatif
      // ou exact) restant le contenu texte. Ce nœud est partagé topic + réponses → couvre les deux.
      const authorDateElt = !date ? null : (
        <Tooltip
          content={
            <FormattedDate
              value={date}
              year="numeric"
              month="long"
              day="numeric"
              weekday="long"
              hour="numeric"
              minute="numeric"
            />
          }
        >
          {dateTimeIso ? (
            <time dateTime={dateTimeIso} className="formattedDate1">
              {formattedDate}
            </time>
          ) : (
            <span className="formattedDate1">{formattedDate}</span>
          )}
        </Tooltip>
      );
      // e.g. "le 30/12/2022" or "il y a 1 h"
      // suppressHydrationWarning because "27 seconds ago" on the server can become "28 seconds ago" on the client, generating a warning.
      const authorDateWithArticleElt = !authorDateElt ? null : (
        <div className={twMerge('authorDate1 inline-flex text-sub', answeredAuthor && 'ml-[33px]')}>
          <span
            className={twMerge(
              'text-gray-soft cursor-default leading-[20px]',
              answeredAuthor && 'leading-[10px]',
            )}
            suppressHydrationWarning
          >
            {relativeOrExact === 'relative' ? (
              authorDateElt
            ) : (
              <FormattedMessage
                {...timeMessages.authorDate}
                values={{
                  formattedDate: authorDateElt,
                }}
              />
            )}
          </span>
        </div>
      );

      return answeredAuthor ? (
        <>
          <AuthorAvatarPseudo
            author={answeredAuthor}
            userId={(answeredAuthor as User).id}
            profile={(answeredAuthor as User)?.profile}
            isUserBlocked={isAnsweredAuthorBlocked}
            withProfileLink
            className="AvatarPseudo1 max-w-full"
          />{' '}
          <div className="authorDate2 flex min-w-0 max-w-full gap-x-1 items-start mt-1">
            <CustomAnswerLead />
            <div className="answeredAuthor flex flex-col max-w-full truncate">
              <FormattedMessage
                {...timeMessages.answeredAuthorDate}
                values={{
                  renderAnsweredAuthor: renderAnsweringAuthorAvatarPseudo,
                  authorDate: authorDateWithArticleElt,
                }}
              />
            </div>
          </div>
        </>
      ) : isNestedAnswer && !isRoot ? (
        <>
          <AuthorAvatarPseudo
            author={author}
            userId={(author as User).id}
            profile={(author as User).profile}
            skipAvatar
            firstChild
            withProfileLink
            className={classNames('AvatarPseudo3 max-w-full', others.className)}
            isUserBlocked={isAuthorBlocked}
          />{' '}
          {authorDateWithArticleElt && (
            <div className="authorDate3 flex flex-row space-x-[10px] items-center min-w-0">
              {authorDateWithArticleElt}
            </div>
          )}
        </>
      ) : (
        <>
          {renderAuthorAvatar}
          <div className="authorDate4 flex-1 flex flex-col min-w-0 text-sm">
            <AuthorPseudo
              author={author}
              userId={(author as User).id}
              profile={(author as User).profile}
              withProfilePopover
              withProfileLink
              containerClassName="inline truncate"
              withStatusBadge
              className="AuthorPseudo1"
            />
            {authorDateWithArticleElt && (
              <div className="flex flex-row space-x-[10px] items-center">
                {authorDateWithArticleElt}
              </div>
            )}
          </div>
        </>
      );
    },
    [
      answeredAuthor,
      author,
      date,
      dateTimeIso,
      isNestedAnswer,
      isRoot,
      others.className,
      renderAnsweringAuthorAvatarPseudo,
      renderAuthorAvatar,
      isAnsweredAuthorBlocked,
      isAuthorBlocked,
    ],
  );

  return <LimitedRelativeTime date={date} render={renderAuthoringDetails} />;
};
